import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, MapIcon, UserIcon, CubeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { ThumbnailData } from '../utils/thumbnailUtils';

interface ThumbnailDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  thumbnail: ThumbnailData | null;
}

/**
 * 缩略图详情弹窗
 * 显示元素的大图预览、名称、描述和提示词
 */
export default function ThumbnailDetailDialog({
  isOpen,
  onClose,
  thumbnail,
}: ThumbnailDetailDialogProps) {
  const { t } = useTranslation(['script', 'common']);

  if (!thumbnail) return null;

  // 根据类型获取图标和颜色
  const getTypeConfig = () => {
    switch (thumbnail.type) {
      case 'scene':
        return {
          icon: MapIcon,
          label: t('script:thumbnail.scene'),
          bgColor: 'bg-blue-500/20',
          textColor: 'text-blue-400',
          borderColor: 'border-blue-500/30',
        };
      case 'character':
        return {
          icon: UserIcon,
          label: t('script:thumbnail.character'),
          bgColor: 'bg-green-500/20',
          textColor: 'text-green-400',
          borderColor: 'border-green-500/30',
        };
      case 'prop':
        return {
          icon: CubeIcon,
          label: t('script:thumbnail.prop'),
          bgColor: 'bg-purple-500/20',
          textColor: 'text-purple-400',
          borderColor: 'border-purple-500/30',
        };
    }
  };

  const typeConfig = getTypeConfig();
  const TypeIcon = typeConfig.icon;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* 背景遮罩 */}
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      {/* 弹窗容器 */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl bg-bg-page rounded-2xl border border-border-subtle shadow-2xl">
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${typeConfig.bgColor} rounded-lg`}>
                <TypeIcon className={`w-5 h-5 ${typeConfig.textColor}`} />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold text-gray-50">
                  {thumbnail.name}
                </DialogTitle>
                <span
                  className={`inline-block mt-1 px-2 py-0.5 text-xs ${typeConfig.bgColor} ${typeConfig.textColor} rounded`}
                >
                  {typeConfig.label}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-bg-elevated rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* 大图预览 */}
            <div className="relative w-full aspect-video bg-bg-elevated rounded-lg overflow-hidden border border-border-subtle">
              {thumbnail.imageUrl ? (
                <img
                  src={thumbnail.imageUrl}
                  alt={thumbnail.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // 图片加载失败时显示占位符
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.classList.add('flex', 'items-center', 'justify-center');
                      const placeholder = document.createElement('div');
                      placeholder.className = 'flex flex-col items-center gap-2 text-gray-400';
                      placeholder.innerHTML = `
                        <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="${thumbnail.type === 'scene'
                          ? 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
                          : thumbnail.type === 'character'
                            ? 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                            : 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                        }" />
                        </svg>
                        <span class="text-sm">${t('script:thumbnail.imageLoadFailed')}</span>
                      `;
                      parent.appendChild(placeholder);
                    }
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <TypeIcon className="w-16 h-16 mb-2" />
                  <span className="text-sm">{t('script:thumbnail.noImage')}</span>
                </div>
              )}
            </div>

            {/* 描述 */}
            {thumbnail.description && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t('script:thumbnail.description')}
                </label>
                <div className="p-3 bg-bg-elevated border border-border-subtle rounded-lg">
                  <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {thumbnail.description}
                  </p>
                </div>
              </div>
            )}

            {/* 提示词 */}
            {thumbnail.prompt && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t('script:thumbnail.aiPrompt')}
                </label>
                <div className="p-3 bg-bg-elevated border border-border-subtle rounded-lg font-mono">
                  <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                    {thumbnail.prompt}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 底部按钮 */}
          <div className="flex justify-end p-4 border-t border-border-subtle">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-300 bg-bg-elevated hover:bg-bg-surface rounded-lg transition-colors"
            >
              {t('script:thumbnail.close')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
